import { Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Facility } from '../../../services/firebase-utilities.service';
import { UIMessagingService } from '../../../services/uimessaging.service';
import { MailerService } from '../../../services/mailer.service';

export interface EditFacilityOwnerDialogData {
  user: { uid?: string; name?: string; userName?: string; email?: string; role?: string; [key: string]: any };
  facility: Facility & { [key: string]: any };
  hubFacilityName?: string;
}

@Component({
  selector: 'app-edit-facility-owner-dialog',
  templateUrl: './edit-facility-owner-dialog.component.html',
  styleUrls: ['./edit-facility-owner-dialog.component.scss'],
  standalone: false
})
export class EditFacilityOwnerDialogComponent {
  form: UntypedFormGroup;
  saving = false;

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<EditFacilityOwnerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditFacilityOwnerDialogData,
    private uiMessaging_$: UIMessagingService,
    private mailer_$: MailerService,
    private http: HttpClient
  ) {
    const u = data.user;
    this.form = this.fb.group({
      name: [u?.name || u?.userName || '', [Validators.required]],
      email: [{ value: u?.email || '', disabled: true }, [Validators.required, Validators.email]]
    });
  }

  async onSave(): Promise<void> {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    try {
      await this.callUpdateUserCF();
      this.uiMessaging_$.toastMessage('User updated successfully', 'SUCCESS');
      this.dialogRef.close({ saved: true });
    } catch (error) {
      console.error('Error updating user:', error);
      this.uiMessaging_$.toastMessage('Error updating user. Please try again.', 'ERROR');
    } finally {
      this.saving = false;
    }
  }

  async onSaveAndInvite(): Promise<void> {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    try {
      const spokeFacilityName = this.data.facility?.name || this.data.facility?.id || 'your facility';
      const hubFacilityName = this.data.hubFacilityName || 'NuageDX';
      const { invitationToken } = await this.callUpdateUserCF({
        facilityId: this.data.facility?.id || null,
        facilityName: spokeFacilityName,
      });
      await this.mailer_$.sendFacilityNetworkInvitation({
        recipientEmail: (this.data.user?.email || '').trim().toLowerCase(),
        spokeFacilityName,
        hubFacilityName,
        invitationToken: invitationToken!,
      });
      this.uiMessaging_$.toastMessage('User updated and invitation sent successfully', 'SUCCESS');
      this.dialogRef.close({ saved: true, invited: true });
    } catch (error) {
      console.error('Error during save and invite:', error);
      this.uiMessaging_$.toastMessage('Error: ' + ((error as any)?.message || 'Please try again.'), 'ERROR');
    } finally {
      this.saving = false;
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private async callUpdateUserCF(
    invitation?: { facilityId: string | null; facilityName: string }
  ): Promise<{ success: boolean; invitationToken?: string }> {
    const { name } = this.form.value;
    const uid = this.data.user?.uid;
    if (!uid) throw new Error('User UID is missing');

    const body: Record<string, unknown> = {
      uid,
      name: name.trim(),
    };
    if (invitation) {
      body['invitation'] = invitation;
    }

    return firstValueFrom(
      this.http.post<{ success: boolean; invitationToken?: string }>(
        `${environment.constants.cloudfunctionsURL}admin-updateUserProfile`,
        body
      )
    );
  }
}
